import React, { useEffect, useId, useState } from 'react';
import * as am5 from '@amcharts/amcharts5';
import * as am5xy from '@amcharts/amcharts5/xy';
import am5themes_Dark from '@amcharts/amcharts5/themes/Dark';
import am5themes_Responsive from '@amcharts/amcharts5/themes/Responsive';

import am5locales_en_US from '@amcharts/amcharts5/locales/en_US';
import am5locales_fr_FR from '@amcharts/amcharts5/locales/fr_FR';
import AppCustomIcon from '../../../../app/features/icon/AppIcon';

import { Grid, Button, Typography, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import CDateRangePicker from './CDateRangePicker';
import moment, { Moment } from 'moment';
import _ from 'lodash';
import CAppSubTitle from '../../../../app/components/CAppSubTitle';
import CAppSwitch from '../../../../app/components/CAppSwitch';
import langInvestFundPerformancesListedAssetsLang from '../../langs/langInvestFundPerformancesListedAssetsLang';
import { IFundPerformancesGraphItem } from '../../interfaces/fund/performances/IFundPerformancesCapitalSG';
import IFund from '../../interfaces/fund/IFund';
import { convertCurrencyLettersToSymbol } from '../../../../app/utils/currency';
type FullDataGraph = {
	date: number;
	share: number | null;
	performance: number | null;
	index: number | null;
};
import * as XLSX from 'xlsx';
import { getLatestNonNullValue } from '../../../../app/utils/utils';

const exportVLToXLSX = (
	data: IFundPerformancesGraphItem[],
	fund: IFund,
	startDate: Moment,
	endDate: Moment,
	vlLabel = 'vl',
	vlFile: 'vl',
) => {
	const xlsxData = data
		.filter((x) => x.share !== null && x.share !== undefined)
		.filter((x) => moment(x.date).isBetween(startDate, endDate, 'days', '[]'))
		.map((item) => ({
			date: moment(item.date).format('DD/MM/YYYY'),
			[vlLabel]: item.share,
		}));
	const worksheet = XLSX.utils.json_to_sheet(xlsxData);

	worksheet['!cols'] = [{ wch: 11 }, { wch: 20 }];
	const workbook = XLSX.utils.book_new();
	XLSX.utils.book_append_sheet(workbook, worksheet, 'Fund Data');
	XLSX.writeFile(workbook, `export_${vlFile}_${fund.name}_${moment().format('YYYYMMDD')}.xlsx`);
};

const getBenchmarkData = (data: IFundPerformancesGraphItem[], startDate: Moment): FullDataGraph[] => {
	const chartData: FullDataGraph[] = [];
	let i = 0;

	// Find t0, fill data with 0 value
	for (i; i < data.length && moment(data[i]?.date).isBefore(startDate); i++) {
		const date = data[i]?.date;
		if (date) chartData?.push({ date: date as number, share: null, index: null, performance: null });
	}
	// If its first value
	i = i === 0 ? 0 : i - 1;

	// (t/t0) *

	// Finding the next t0 if no date
	let iIndex = i;
	for (iIndex; iIndex < data.length && data[iIndex].share === null; iIndex++) {
		/* empty */
	}
	const t0Index = _.divide((data[iIndex]?.share as number) ?? 0, 100);

	let iPerf = i;
	for (iPerf; iPerf < data.length && data[iPerf].performance === null; iPerf++) {
		/* empty */
	}
	const t0Performance = _.divide((data[iPerf]?.performance as number) ?? 0, 100);

	for (i; i < data.length; i++) {
		const ti = data[i];
		chartData?.push({
			date: data[i]?.date as number,
			share: data[i]?.share ?? null,
			index:
				ti.performance !== null && t0Performance !== null
					? _.floor(_.divide(ti.performance as number, t0Performance), 3) - 100
					: null,
			performance:
				ti.share !== null && t0Index !== null ? _.floor(_.divide(ti.share as number, t0Index), 3) - 100 : null,
		});
	}
	return chartData;
};

type CGraphAssetsTrendProps = {
	title: string | React.ReactNode;
	dataset: IFundPerformancesGraphItem[];
	fund: IFund;
};

const CGraphAssetsTrend = (props: CGraphAssetsTrendProps) => {
	const { title, fund, dataset } = props;
	const idGraph = useId();
	const { t, i18n } = useTranslation();
	const [isBenchmark, setIsBenchmark] = useState<boolean>(false);

	const theme = useTheme();
	const darkModeEnabled = theme.palette.mode === 'dark';

	const minDate = moment(_.first(dataset)?.date);

	const latestNonNullValue = isBenchmark
		? getLatestNonNullValue(dataset, 'performance')
		: getLatestNonNullValue(dataset, 'share');

	const maxDate = moment(latestNonNullValue?.date);
	const [startDate, setStartDate] = useState<Moment | null>(
		moment.max(minDate, maxDate.clone().subtract(1, 'years')),
	);
	const [endDate, setEndDate] = useState<Moment | null>(moment(maxDate));
	const [chartXAxis, setChartXAxis] = useState<am5xy.DateAxis<am5xy.AxisRenderer>>();
	useEffect(() => {
		const root = am5.Root.new(`multiple-lines-chart-${idGraph}`);
		if (darkModeEnabled) {
			root.setThemes([am5themes_Dark.new(root), am5themes_Responsive.new(root)]);
		} else {
			root.setThemes([am5themes_Responsive.new(root)]);
		}

		if (i18n.language === 'fr') {
			root.locale = am5locales_fr_FR;
		} else {
			root.locale = am5locales_en_US;
		}

		const chart = root.container.children.push(
			am5xy.XYChart.new(root, {
				panX: false,
				panY: false,
				wheelX: 'panX',
				wheelY: 'zoomX',
				paddingLeft: 0,
			}),
		);

		// Add cursor
		const cursor = chart.set(
			'cursor',
			am5xy.XYCursor.new(root, {
				behavior: 'zoomX',
			}),
		);
		cursor.lineY.set('visible', false);

		// Tooltip
		const tooltip = am5.Tooltip.new(root, {
			getFillFromSprite: false,
			autoTextColor: false,
			pointerOrientation: 'horizontal',
			labelText:
				`[fontSize: 16px; fontWeight:600]{date.formatDate('dd MMM yyyy')}[/][/]\n` +
				`${
					isBenchmark
						? `[fontSize: 15px; width:200px]Performance[/][#2ABDBB; fontWeight:600]{performance}%[/]\n` +
						  `[fontSize: 15px; width:200px]${t(
								langInvestFundPerformancesListedAssetsLang.benchmark.tag,
						  )}[/][#B7BFCB; fontWeight:600]{index}%[/]`
						: `[fontSize: 15px; width:200px]${
								fund.name
						  }[/][#2ABDBB; fontWeight:600]{share}${convertCurrencyLettersToSymbol(
								fund.currency ?? '',
						  )}[/]\n`
				}`,
		});

		tooltip.get('background')?.setAll({
			fill: am5.color(darkModeEnabled ? '#fff' : '#1A2B48'),
			stroke: am5.color(darkModeEnabled ? '#fff' : '#1A2B48'),
			fillOpacity: darkModeEnabled ? 0.95 : 0.8,
		});

		// xAxis
		const xAxis = chart.xAxes.push(
			am5xy.DateAxis.new(root, {
				groupData: false,
				maxDeviation: 0,
				baseInterval: {
					timeUnit: 'day',
					count: 1,
				},
				markUnitChange: false,
				renderer: am5xy.AxisRendererX.new(root, {}),
			}),
		);

		const xAxisDateFormats = xAxis.get('dateFormats');
		if (xAxisDateFormats) {
			xAxisDateFormats['month'] = 'MMM\nyyyy';
			xAxisDateFormats['week'] = 'dd MMM\nyyyy';
			xAxisDateFormats['day'] = 'dd MMM\nyyyy';
		}

		xAxis.get('renderer').labels.template.setAll({
			centerY: am5.p50,
			paddingTop: 10,
			paddingBottom: 20,
			paddingRight: 15,
			textAlign: 'center',
			fontSize: '13px',
			fontFamily: "'Poppins', sans-serif",
			fontWeight: '500',
			fill: am5.color('#1A2B48'),
		});

		// yAxis
		const yAxis = chart.yAxes.push(
			am5xy.ValueAxis.new(root, {
				numberFormat: isBenchmark ? "#'%'" : '#',
				renderer: am5xy.AxisRendererY.new(root, {}),
			}),
		);
		yAxis.get('renderer').labels.template.setAll({
			centerY: am5.p50,
			paddingTop: 10,
			paddingBottom: 20,
			paddingRight: 15,
			textAlign: 'center',
			fontSize: '10px',
			fontFamily: "'Poppins', sans-serif",
			fontWeight: '400',
			fill: am5.color('#AAAAAA'),
		});

		const series = chart.series.push(
			am5xy.LineSeries.new(root, {
				name: isBenchmark ? t(langInvestFundPerformancesListedAssetsLang.performance.tag) : fund.name,
				xAxis: xAxis,
				yAxis: yAxis,
				valueYField: isBenchmark ? 'performance' : 'share',
				valueXField: 'date',
				tooltip: tooltip,
				stroke: am5.color(0x2abdbb),
				legendLabelText: '{name}',
			}),
		);
		series.strokes.template.setAll({
			strokeWidth: 2,
		});

		const scrollbar = am5xy.XYChartScrollbar.new(root, {
			orientation: 'horizontal',
			height: 50,
			marginTop: 50,
		});
		chart.set('scrollbarX', scrollbar);
		chart.bottomAxesContainer.children.push(scrollbar);

		// Box window
		const sbxAxis = scrollbar.chart.xAxes.push(
			am5xy.DateAxis.new(root, {
				maxDeviation: 0,
				baseInterval: { timeUnit: 'day', count: 1 },
				renderer: am5xy.AxisRendererX.new(root, {
					opposite: false,
					strokeOpacity: 0,
				}),
			}),
		);

		const sbyAxis = scrollbar.chart.yAxes.push(
			am5xy.ValueAxis.new(root, {
				renderer: am5xy.AxisRendererY.new(root, {}),
			}),
		);

		const sbseries = scrollbar.chart.series.push(
			am5xy.LineSeries.new(root, {
				xAxis: sbxAxis,
				yAxis: sbyAxis,
				valueYField: isBenchmark ? 'performance' : 'share',
				valueXField: 'date',
				stroke: am5.color(0x2abdbb),
				fill: am5.color(0x2abdbb),
			}),
		);

		sbseries.fills.template.setAll({
			fillOpacity: 0.1,
			visible: true,
		});

		sbseries.strokes.template.setAll({
			strokeWidth: 2,
		});

		let seriesBenchmark: am5xy.LineSeries;
		if (isBenchmark) {
			seriesBenchmark = chart.series.push(
				am5xy.LineSeries.new(root, {
					name: t(langInvestFundPerformancesListedAssetsLang.benchmark.tag),
					xAxis: xAxis,
					yAxis: yAxis,
					valueYField: 'index',
					valueXField: 'date',
					stroke: am5.color(0xb7bfcb),
					legendLabelText: '{name}',
				}),
			);

			seriesBenchmark.strokes.template.setAll({
				strokeWidth: 2,
			});
			const newData = getBenchmarkData(dataset, startDate as Moment);
			series.data.setAll(newData);
			seriesBenchmark.data.setAll(newData);
			sbseries.data.setAll(dataset);
			seriesBenchmark.appear();

			yAxis.children.unshift(
				am5.Label.new(root, {
					rotation: -90,
					fontFamily: 'Poppins',
					text: t(langInvestFundPerformancesListedAssetsLang.benchmark.tag),
					fontSize: '0.9em',
					y: am5.p50,
					centerX: am5.p50,
				}),
			);
		} else {
			series.data.setAll(dataset);
			sbseries.data.setAll(dataset);
			yAxis.children.unshift(
				am5.Label.new(root, {
					rotation: -90,
					fontFamily: 'Poppins',
					text: t(langInvestFundPerformancesListedAssetsLang.vl_chart_legend.tag),
					fontSize: '0.9em',
					y: am5.p50,
					centerX: am5.p50,
				}),
			);
		}

		const legend = chart.children.push(
			am5.Legend.new(root, { centerX: am5.p50, x: am5.p50, y: chart.height() - 125 }),
		);
		legend.data.setAll(chart.series.values);
		scrollbar.on('start', () => {
			const newStartDate = moment(xAxis?.getPrivate('selectionMinFinal'));
			setStartDate(newStartDate);
			if (isBenchmark) {
				const newData = getBenchmarkData(dataset, newStartDate);
				series.data.clear();
				series.data.setAll(newData);
				seriesBenchmark.data.clear();
				seriesBenchmark?.data.setAll(newData);
			}
		});

		scrollbar.on('end', () => {
			const newEndDate = moment(xAxis?.getPrivate('selectionMaxFinal'));
			setEndDate(newEndDate);
		});
		series.events.once('datavalidated', () => {
			if (startDate && endDate) {
				xAxis.zoomToDates(startDate.toDate(), endDate.toDate());
			}
		});

		setChartXAxis(xAxis);

		return () => root.dispose();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [darkModeEnabled, i18n.language, isBenchmark, dataset]);

	const onZoom = (newStartDate: Moment, newEndDate: Moment) => {
		if (
			!moment(chartXAxis?.getPrivate('selectionMinFinal')).isSame(newStartDate) ||
			!moment(chartXAxis?.getPrivate('selectionMaxFinal')).isSame(newEndDate)
		) {
			setStartDate(newStartDate);
			setEndDate(newEndDate);
			chartXAxis?.zoomToDates(newStartDate.toDate(), newEndDate.toDate());
		}
	};

	return (
		<div>
			<Grid container alignItems="center" justifyContent="space-between" margin="1em 0">
				<Grid item>
					<CAppSubTitle
						title={t(langInvestFundPerformancesListedAssetsLang.graph_title.tag)}
						subtitle={t(langInvestFundPerformancesListedAssetsLang.at_date.tag, {
							date: moment(latestNonNullValue?.date).format('DD/MM/YYYY'),
						})}
						sx={{ fontSize: '1.3em', margin: 0 }}
					/>
				</Grid>

				<Grid item>
					<div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
						<div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
							{!isBenchmark && (
								<Button
									variant="outlined"
									size="small"
									startIcon={<AppCustomIcon tag="export_data" />}
									color="primary"
									onClick={() =>
										exportVLToXLSX(
											dataset,
											fund,
											startDate as Moment,
											endDate as Moment,
											t(langInvestFundPerformancesListedAssetsLang.vl_chart_legend.tag),
											t(langInvestFundPerformancesListedAssetsLang.vl.tag) as 'vl',
										)
									}
								>
									{t(langInvestFundPerformancesListedAssetsLang.export_data.tag)}
								</Button>
							)}
						</div>

						<div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
							<CDateRangePicker
								startDate={startDate as Moment}
								endDate={endDate as Moment}
								minDate={minDate}
								maxDate={maxDate}
								onChange={onZoom}
							/>
						</div>
					</div>
				</Grid>
			</Grid>
			<Grid container alignItems="flex-start" justifyContent="space-between" margin="1em 0">
				<Grid item>
					<CAppSwitch
						sx={{ '& .MuiSwitch-track': { backgroundColor: '#c5ccd5' } }}
						checked={isBenchmark}
						onChange={() => setIsBenchmark(!isBenchmark)}
					/>{' '}
					{t(langInvestFundPerformancesListedAssetsLang.benchmark.tag)}
				</Grid>
				<Grid item>
					<Typography fontWeight={500} color="#6F8098" fontSize="1em">
						{title}
					</Typography>
				</Grid>
			</Grid>

			<div
				id={`multiple-lines-chart-${idGraph}`}
				style={{ height: '450px', margin: '0px', padding: '0px', fontFamily: 'Poppins' }}
			/>
		</div>
	);
};

export default CGraphAssetsTrend;
